import { type Dispatch, useEffect, useRef } from 'react';

import type {
    OperateWizardAction,
    WizardDestination,
} from '@/routes/Internal/routes/Client/FishGroupsOverview/actions/operations/operateWizardReducer';

interface MergeTargetPrefillInput {
    dispatch: Dispatch<OperateWizardAction>;
    mergeTargetDestination: WizardDestination | null;
    secondSourceId: number | null;
    continuationCountRaw: string;
    initialPopulation: number | null | undefined;
    secondSourcePopulation: number | null | undefined;
}

/**
 * Seeds the merge target stub on mount and prefills its estimated count once both
 * populations are known.
 */
export function useMergeTargetPrefill(input: MergeTargetPrefillInput): void {
    const {
        dispatch,
        mergeTargetDestination,
        secondSourceId,
        continuationCountRaw,
        initialPopulation,
        secondSourcePopulation,
    } = input;

    // Seed an empty stub for destinations[1] on mount so the Name input
    // below has a stable target to dispatch against, even before the
    // operator picks a cage. Idempotent — the reducer skips if a real
    // target already exists (e.g. after navigating back from a later step).
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally mount-only
    useEffect(() => {
        dispatch({ type: 'initMergeTargetStub' });
    }, []);

    // Unified count prefill for both branches. The formula is
    //     (initialPopulation − continuationCount) + secondSourcePopulation
    // where `secondSource` is whichever FG ends up at sources[1] — the
    // occupant of the chosen cage on the occupied branch, or the operator-
    // chosen absorbed FG on the empty branch. Opt-out conditions:
    //   - continuation count not yet entered (no basis for the split),
    //   - either population still loading (or unknown),
    //   - the merge-target count input already has a value (don't fight a
    //     manual edit).
    // The ref keeps a refill from undoing a clear-by-the-operator: once we
    // prefill for a given second-source FG, we don't prefill again until
    // that FG changes.
    const lastPrefilledSourceRef = useRef<number | null>(null);
    useEffect(() => {
        if (secondSourceId == null) {
            lastPrefilledSourceRef.current = null;
            return;
        }
        if (!mergeTargetDestination || mergeTargetDestination.cageId == null) return;
        if (lastPrefilledSourceRef.current === secondSourceId) return;

        const continuationCount = Number(continuationCountRaw);
        if (!Number.isFinite(continuationCount) || continuationCount <= 0) return;
        if (initialPopulation == null || secondSourcePopulation == null) return;

        const splitToMerge = initialPopulation - continuationCount;
        if (splitToMerge <= 0) return;
        const total = splitToMerge + secondSourcePopulation;
        if (!Number.isFinite(total) || total <= 0) return;

        // Mark as handled BEFORE checking the current value: if the operator
        // clears the prefill, we won't refill until the source changes.
        lastPrefilledSourceRef.current = secondSourceId;
        if (mergeTargetDestination.estimatedFishCount !== '') return;
        dispatch({
            type: 'setDestinationEstimatedCount',
            tempId: mergeTargetDestination.tempId,
            value: String(total),
        });
    }, [
        secondSourceId,
        mergeTargetDestination,
        continuationCountRaw,
        initialPopulation,
        secondSourcePopulation,
        dispatch,
    ]);
}
